'use client'

import React, { useEffect, useState } from 'react'
import AppStoreLink, { APP_STORE_URL, type AppStoreLinkProps } from '@/app/components/AppStoreLink'

/**
 * EventStoreLink — App Store CTA for in-app events.
 *
 * Apple's web store hard-404s ?eventid= URLs until the event's publish
 * start, so linking the event URL early would send visitors to a dead page.
 * This wrapper renders the plain app URL first and upgrades to the
 * ?eventid= deep link in the browser once the publish moment has passed.
 * The upgrade happens in an effect (not at render) so server HTML and the
 * first client render always match — no hydration mismatch, and pages never
 * need a rebuild when an event goes live.
 */
export default function EventStoreLink({
  eventId,
  publishAtIso,
  ...linkProps
}: Omit<AppStoreLinkProps, 'href'> & {
  eventId: string
  publishAtIso: string
}) {
  const [href, setHref] = useState<string>(APP_STORE_URL)

  useEffect(() => {
    if (Date.now() >= Date.parse(publishAtIso)) {
      // Deliberate. The href depends on wall-clock time. Computing it during
      // render would bake the build-time answer into the prerendered HTML and
      // then disagree with the browser at hydration.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHref(`${APP_STORE_URL}?eventid=${eventId}`)
    }
  }, [eventId, publishAtIso])

  return <AppStoreLink {...linkProps} href={href} />
}
